import { DeviceStateFlag } from '@racemap/utilities/consts/events';
import { formatDateAndTime, formatTimeDuration } from '@racemap/utilities/formattings/formatting';
import { formatDistance } from '@racemap/utilities/formattings/units';
import {
  type Geofence,
  type PreparedStarterInfo,
  sortTimes,
  testEventHasVirtDuration,
} from '@racemap/utilities/functions/timing';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { SplitObject } from '@racemap/utilities/types/geos';
import { Unit } from '@racemap/utilities/types/units';
import type { Immutable } from 'immer';
import { ICON_CDN_PATH } from '../../../BasicComponents/IconChooser/IconChooser';
import type { Column } from '../TableBody';
import { Cell } from './BasicCell';
import { LARGE_TIME_COL_WIDTH, TimesColumHeaderTitle } from './TimesCol';

function StartTimesColComponent({ starterInfo }: { starterInfo: Immutable<PreparedStarterInfo> }) {
  return (
    <Cell
      style={{
        opacity: starterInfo.state === DeviceStateFlag.DISQUALIFIED ? 0.7 : 1,
        fontStyle: starterInfo.state === DeviceStateFlag.DISQUALIFIED ? 'italic' : 'normal',
      }}
    >
      {starterInfo.results[0]?.time != null ? formatDateAndTime(starterInfo.results[0]?.time) : '-'}
    </Cell>
  );
}

export function getStartTimesCol(
  event: Immutable<RacemapEvent>,
  splits: Immutable<Array<SplitObject>>,
  timekeepings: Immutable<Array<Geofence>>,
): Column {
  const startGeofence = timekeepings[0];
  if (startGeofence == null) {
    return {
      key: 'times_start',
      title: 'Start Time',
      width: LARGE_TIME_COL_WIDTH,
      render: function renderer() {
        return <Cell>-</Cell>;
      },
    };
  }

  const eventHasVirtDuration = testEventHasVirtDuration(event);
  const unitType = event.playerOptions.unitType;
  const visibleElements = event.modules.timing.visibleElements;

  const belongingSplit = splits.find((split) => split.id === startGeofence.id);
  const icon = belongingSplit?.properties.icon;
  const name = splits == null ? 'Start' : belongingSplit?.properties.name;
  const distance = eventHasVirtDuration
    ? formatTimeDuration(startGeofence.timeOffset)
    : formatDistance({
        value: startGeofence.offset ?? 0,
        inputUnit: Unit.Distance.m,
        unitType,
      });
  return {
    key: 'times_start',
    title: (
      <TimesColumHeaderTitle
        headerContent={visibleElements.headerContent}
        name={name}
        icon={icon != null ? icon : `${ICON_CDN_PATH}/start-02_tickless.svg`}
        distance={distance}
      />
    ),
    width: LARGE_TIME_COL_WIDTH,
    render: function renderer(row: Immutable<PreparedStarterInfo>) {
      return <StartTimesColComponent starterInfo={row} />;
    },
    sorter: (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) =>
      sortTimes(a?.results[0]?.time, b?.results[0]?.time),
  };
}
